// packages/kern/src/svg.ts — Szene als Zeichenkette (SPEC §3.10).
//
// KEIN DOM: außer `text-canvas.ts` darf keine Datei in `packages/kern/src`
// `document`, `window`, `navigator`, `fetch`, `localStorage` oder einen
// React-Import enthalten. Diese Datei baut SVG als TEXT, nicht über ein DOM.
//
// Knoten mit `ed: true` werden hier ÜBERSPRUNGEN — der Schalter sitzt im
// Serializer, nicht in einer DOM-Reinigung hinterher. Zahlen werden mit
// `String(n)` geschrieben: KEINE Rundung, KEIN toFixed (die Goldens enthalten
// `x="202.33333333333331"`).
//
// Vorlage: `docs/quellen/understand/prototype.json`, reusable_snippets[21]
// (`svgString()`/`fileBase()`), Aufbau und Maße aus §3.10.

import { PAD_X } from "./layout";
import type { Knoten, Kopfzeile, Szene } from "./scene";
import { CHART_FAM, CMUTED, PAPER, PETROL } from "./styles";
import { TB } from "./texte";

export interface SvgOptionen {
  /** @font-face-Regeln mit data-URI. Leer = keine <style>-Zeile. */
  fontCss?: string;
  /** Kopfzeile ueber dem Blatt (Exportbeiwerk), Hoehe KOPF_H. */
  kopfzeile?: Kopfzeile | null;
  /** false = auch das Kopfband bleibt ohne Papier (I8). Vorgabe true. */
  hintergrund?: boolean;
}

export const KOPF_H = 44;

const SVG_NS = "http://www.w3.org/2000/svg";
const XLINK_NS = "http://www.w3.org/1999/xlink";

/** Maskierung im Textinhalt (§3.10). */
function textEsc(s: string): string {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Maskierung in Attributwerten — zusätzlich das doppelte Anführungszeichen. */
function attrEsc(s: string): string {
  return textEsc(s).replace(/"/g, "&quot;");
}

/** Ein Attribut, wenn der Wert gesetzt ist. `undefined` und `null` fallen weg. */
function attr(name: string, wert: string | number | undefined | null): string {
  if (wert === undefined || wert === null) return "";
  return " " + name + '="' + attrEsc(String(wert)) + '"';
}

/**
 * Ein Knoten als Zeichenkette. Knoten mit `ed: true` ergeben "" — samt
 * ihren Kindern.
 *
 * Die Gruppen-Kennung wird als `data-id` geschrieben, nicht als `id`: so macht
 * es der Prototyp (und damit die Goldens), so macht es `Blatt.tsx` (§5.6.1),
 * und `uid()` liefert Zeichenketten, die mit einer Ziffer beginnen können —
 * als XML-`id` wären die ungültig.
 */
function knotenText(n: Knoten): string {
  if (n.ed) return "";
  switch (n.k) {
    case "rect": {
      const a = attr("x", n.x) + attr("y", n.y) + attr("width", n.width) + attr("height", n.height)
        + attr("rx", n.rx) + attr("fill", n.fill) + attr("opacity", n.opacity)
        + attr("stroke", n.stroke) + attr("stroke-width", n.strokeWidth)
        + attr("stroke-dasharray", n.strokeDasharray);
      // `titel` wird ein Kind <title> — die Kurzhilfe der Ferienbalken.
      const titel = n.titel;
      if (titel === undefined) return "<rect" + a + "/>";
      return "<rect" + a + "><title>" + textEsc(titel) + "</title></rect>";
    }
    case "text": {
      const a = attr("x", n.x) + attr("y", n.y) + attr("text-anchor", n.textAnchor)
        + attr("fill", n.fill) + attr("font-family", n.fontFamily) + attr("font-size", n.fontSize)
        + attr("font-weight", n.fontWeight) + attr("font-style", n.fontStyle)
        + attr("transform", n.transform) + attr("dominant-baseline", n.dominantBaseline);
      return "<text" + a + ">" + textEsc(n.text) + "</text>";
    }
    case "path": {
      const a = attr("d", n.d) + attr("fill", n.fill) + attr("stroke", n.stroke)
        + attr("stroke-width", n.strokeWidth) + attr("stroke-linecap", n.strokeLinecap)
        + attr("stroke-linejoin", n.strokeLinejoin) + attr("fill-rule", n.fillRule);
      return "<path" + a + "/>";
    }
    case "line": {
      const a = attr("x1", n.x1) + attr("y1", n.y1) + attr("x2", n.x2) + attr("y2", n.y2)
        + attr("stroke", n.stroke) + attr("stroke-width", n.strokeWidth) + attr("opacity", n.opacity);
      return "<line" + a + "/>";
    }
    case "image": {
      // `href` bleibt `href` (SVG 2) — KEIN `xlink:href` (§3.10).
      const a = attr("x", n.x) + attr("y", n.y) + attr("width", n.width) + attr("height", n.height)
        + attr("href", n.href);
      return "<image" + a + "/>";
    }
    case "g": {
      const a = attr("data-id", n.id) + attr("transform", n.transform);
      return "<g" + a + ">" + n.kinder.map(knotenText).join("") + "</g>";
    }
  }
}

/** Vollstaendiges SVG-Dokument. Enthaelt NIE Knoten mit ed:true. */
export function sceneToSvgString(szene: Szene, o?: SvgOptionen): string {
  const kopf = o?.kopfzeile ?? null;
  const W = szene.breite;
  const H = szene.hoehe + (kopf ? KOPF_H : 0);
  let s = '<?xml version="1.0" encoding="UTF-8"?>\n';
  s += "<svg" + attr("xmlns", SVG_NS) + attr("xmlns:xlink", XLINK_NS)
    + attr("role", "img") + attr("aria-label", TB.blattAria)
    + attr("viewBox", "0 0 " + W + " " + H) + attr("width", W) + attr("height", H) + ">";
  // Die Schriftregeln müssen als ERSTES Kind stehen, sonst rastert ein
  // Betrachter die erste Zeile noch mit der Ersatzschrift.
  if (o?.fontCss) s += "<style>" + o.fontCss + "</style>";
  const blatt = szene.knoten.map(knotenText).join("");
  if (kopf) {
    // Der Papier-Knoten der Szene (buildScene, Knoten 1) steckt IM verschobenen
    // Gruppenknoten und deckt darum erst ab y = KOPF_H. Ohne diesen Grund hat
    // die heruntergeladene SVG-Datei über dem cremefarbenen Blatt einen
    // durchsichtigen (in Word/InDesign: weißen) Streifen mit Titel und Datum,
    // während PDF (body{background}) und Client-PNG (Canvas mit PAPER gefüllt)
    // gedeckt sind — dasselbe Blatt, drei Formate, zwei Ergebnisse (R1-7).
    // Wer „Hintergrund: Transparent" gewählt hat, bekommt ihn NICHT.
    if (o?.hintergrund !== false) {
      s += knotenText({ k: "rect", x: 0, y: 0, width: W, height: KOPF_H, fill: PAPER });
    }
    s += kopfzeileKnoten(kopf, W).map(knotenText).join("");
    s += '<g transform="translate(0 ' + KOPF_H + ')">' + blatt + "</g>";
  } else {
    s += blatt;
  }
  s += "</svg>";
  return s;
}

/**
 * Nur die Knoten der Kopfzeile, fuer den, der sie selbst setzen will.
 * Beiwerk über dem Blatt — kein Teil des Druckbilds (I7, B10).
 */
export function kopfzeileKnoten(o: Kopfzeile, breite: number): Knoten[] {
  const aus: Knoten[] = [
    { k: "text", x: PAD_X, y: 20, text: o.titel, fill: PETROL, fontFamily: CHART_FAM, fontSize: 13, fontWeight: 500 },
  ];
  // Ohne `kunde` steht dort nur der Stand (I7).
  const links = [o.kunde, o.stand].filter((t): t is string => !!t).join(" · ");
  aus.push({ k: "text", x: PAD_X, y: 34, text: links, fill: CMUTED, fontFamily: CHART_FAM, fontSize: 9.5 });
  aus.push({ k: "text", x: breite - PAD_X, y: 34, text: o.datum, textAnchor: "end", fill: CMUTED, fontFamily: CHART_FAM, fontSize: 9.5 });
  if (o.logo) {
    aus.push({ k: "image", x: breite - PAD_X - 96, y: 6, width: 96, height: 24, href: o.logo });
  }
  aus.push({ k: "line", x1: PAD_X, y1: KOPF_H - 4, x2: breite - PAD_X, y2: KOPF_H - 4, stroke: PETROL, strokeWidth: 0.8, opacity: 0.5 });
  return aus;
}

/** Dateiname aus dem Plantitel, wortgleich zu fileBase() des Prototyps. */
export function dateiBasis(titel: string): string {
  return (titel || TB.dateiFallback).replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, "_").slice(0, 80) || TB.dateiFallback;
}
